import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Collapse, Modal, Spin, message, notification } from 'antd';
import { InfoCircleOutlined, LockOutlined } from '@ant-design/icons';

import { DiContainer } from '@/di/di-container';
import { ClientStorageService } from '@/common/services/client-storage.service';
import {
  useGetCategoriesQuery,
  useGetGroupedStepsQuery,
  useResolveConfigurationMutation,
} from '@/services/configurator.service';
import {
  useSetConfigurationMutation,
  useMarkBunkerAsConfiguredMutation,
  useApplyConfigurationMutation,
} from '@/services/picker.service';
import {
  useAddToShoppingCartMutation,
  useClearShoppingCartMutation,
} from '@/shoppingCart/services/shopping-cart.service';
import { useGetSettingsQuery } from '@/services/settings.service';
import { addSpacesInNumbers } from '@/configurator/helpers/add-spaces-in-numbers';
import { OptionSelector } from '@/feed/components/matrix-modal/components/option-selector';

const clientStorageService = DiContainer.get(ClientStorageService);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any;

interface IOption {
  name: string;
  displayName: string;
  configurationItemId: number;
  cost: number;
  description?: string;
  profitItems?: string[];
  imageUrl?: string;
  mandatory?: boolean; // "Да" во всех модификациях модели → отключить нельзя
}
interface IUnit {
  pickerOptionId: number;
  enabled: Set<string>; // имена включённых опций
}
interface IGroup {
  pickerId: number;
  model: string;
  options: IOption[];
  units: IUnit[];
}

// Строим группы из ответа /configuration_steps/grouped.
function buildGroups(raw: Any[]): IGroup[] {
  return (raw || []).map((g: Any) => {
    const options: IOption[] = (g.items || []).map((it: Any) => {
      const attrs = it.configuration_item?.data?.attributes || {};
      return {
        name: it.name,
        displayName: it.display_name,
        configurationItemId: it.configuration_item_id,
        cost: it.current_cost,
        description: attrs.description,
        profitItems: attrs.profit_items || [],
        imageUrl: attrs.main_image_url,
        mandatory: !!it.mandatory,
      };
    });
    const unitsCount = g.items?.[0]?.steps?.length || 0;
    const units: IUnit[] = Array.from({ length: unitsCount }, (_, u) => {
      const enabled = new Set<string>();
      (g.items || []).forEach((it: Any) => {
        if (it.steps?.[u]?.enabled) enabled.add(it.name);
      });
      return {
        pickerOptionId: g.items?.[0]?.steps?.[u]?.picker_option_id,
        enabled,
      };
    });
    return { pickerId: g.picker_id, model: g.bunker_model, options, units };
  });
}

// Персист выбора Эксперта между перезагрузками (баг 18): выбор идёт через resolve
// без записи на сервер, поэтому до «Оформить заявку» F5 терял галочки. Храним
// набор включённых опций по pickerOptionId в localStorage и накладываем при загрузке.
const SELECTIONS_KEY = (s: string) => `expert_selections_${s}`;

const persistSelections = (s: string, groups: IGroup[]) => {
  const map: Record<string, string[]> = {};
  groups.forEach((g) =>
    g.units.forEach((u) => {
      map[u.pickerOptionId] = [...u.enabled];
    })
  );
  try {
    localStorage.setItem(SELECTIONS_KEY(s), JSON.stringify(map));
  } catch {
    /* localStorage недоступен — не критично */
  }
};

const loadSelections = (s: string): Record<string, string[]> | null => {
  try {
    return JSON.parse(localStorage.getItem(SELECTIONS_KEY(s)) || 'null');
  } catch {
    return null;
  }
};

export const GroupedExpert = () => {
  const session = clientStorageService.getUUID();
  const navigate = useNavigate();

  const { data: categories } = useGetCategoriesQuery({});
  const categoryId = categories?.data?.[0]?.id ?? 1;

  const { data, isLoading, isFetching } = useGetGroupedStepsQuery(
    { session, category_id: categoryId },
    // Перезапрашивать при каждом входе на экран — иначе после смены состава/количества
    // моделей в каталоге групповой набор остаётся закэшированным (нужен был F5).
    { skip: !categoryId, refetchOnMountOrArgChange: true }
  );
  const [resolveConfiguration] = useResolveConfigurationMutation();
  const [setConfiguration] = useSetConfigurationMutation();
  const [markBunkerAsConfigured] = useMarkBunkerAsConfiguredMutation();
  const [applyConfiguration] = useApplyConfigurationMutation();
  const [addToShoppingCart] = useAddToShoppingCartMutation();
  const [clearShoppingCart] = useClearShoppingCartMutation();
  const [submitting, setSubmitting] = useState(false);
  const { data: settings } = useGetSettingsQuery();
  const showPrices = !!settings?.show_prices;

  const [groups, setGroups] = useState<IGroup[]>([]);
  const [applying, setApplying] = useState(false);
  const [infoOption, setInfoOption] = useState<IOption | null>(null);

  useEffect(() => {
    if (!data?.data) return;
    const built = buildGroups(data.data);
    // Накладываем сохранённый выбор (если на сервере шаги ещё не записаны — после F5).
    const saved = loadSelections(session);
    if (saved) {
      built.forEach((g) => {
        const known = new Set(g.options.map((o) => o.name));
        g.units.forEach((u) => {
          const names = saved[u.pickerOptionId];
          if (names && !u.enabled.size) {
            u.enabled = new Set(names.filter((n) => known.has(n)));
          }
        });
      });
    }
    setGroups(built);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Применяет resolve к одному юниту: шлёт выбранные опции, получает пакет.
  const resolveUnit = async (
    group: IGroup,
    unit: IUnit,
    desired: Set<string>
  ): Promise<Set<string>> => {
    const ids = group.options
      .filter((o) => desired.has(o.name))
      .map((o) => o.configurationItemId);
    const res: Any = await resolveConfiguration({
      session,
      picker_option_id: unit.pickerOptionId,
      configuration_item_ids: ids,
    });
    if ('error' in res) return desired;
    const result = res?.data?.data;
    if (!result) return desired;
    const known = new Set(group.options.map((o) => o.name));
    const displayOf = (n: string) =>
      group.options.find((o) => o.name === n)?.displayName || n;

    if (result.conflict) {
      notification.warning({
        message: 'Несовместимый набор опций',
        description: result.conflict.message,
      });
      return unit.enabled; // откатываем
    }
    // Показываем только видимые опции (скрытый базлайн вроде механических заслонок не упоминаем).
    const removedVisible = (result.removed || []).filter((n: string) => known.has(n));
    if (removedVisible.length) {
      notification.info({
        message: `${group.model}: состав изменён`,
        description: `Заменены/исключены: ${removedVisible.map(displayOf).join(', ')}`,
      });
    }
    return new Set<string>(
      [
        ...(result.included || []),
        ...(result.surcharges || []).map((s: Any) => s.internal_name),
      ].filter((n: string) => known.has(n))
    );
  };

  const sig = (s: Set<string>) => [...s].sort().join('|');

  // Тоггл опции для одного юнита.
  const toggleUnit = async (gi: number, ui: number, name: string) => {
    setApplying(true);
    try {
      const group = groups[gi];
      const unit = group.units[ui];
      const desired = new Set(unit.enabled);
      desired.has(name) ? desired.delete(name) : desired.add(name);
      const resolved = await resolveUnit(group, unit, desired);
      setGroups((prev) => {
        const next = prev.map((g, i) =>
          i !== gi
            ? g
            : {
                ...g,
                units: g.units.map((u, j) =>
                  j !== ui ? u : { ...u, enabled: resolved }
                ),
              }
        );
        persistSelections(session, next);
        return next;
      });
    } finally {
      setApplying(false);
    }
  };

  // «Выбрать для всех»: тоггл опции сразу для всех юнитов группы.
  // Юниты модели идентичны (одни правила/модификации) → resolve вызываем ОДИН раз
  // на уникальный набор опций (обычно 1 запрос вместо N).
  const toggleAll = async (gi: number, name: string, value: boolean) => {
    setApplying(true);
    try {
      const group = groups[gi];
      const desiredByUnit = group.units.map((u) => {
        const d = new Set(u.enabled);
        value ? d.add(name) : d.delete(name);
        return d;
      });
      const cache = new Map<string, Promise<Set<string>>>();
      const resolvedUnits = await Promise.all(
        group.units.map((unit, i) => {
          const key = sig(desiredByUnit[i]);
          if (!cache.has(key)) {
            cache.set(key, resolveUnit(group, unit, desiredByUnit[i]));
          }
          return cache.get(key) as Promise<Set<string>>;
        })
      );
      setGroups((prev) => {
        const next = prev.map((g, i) =>
          i !== gi
            ? g
            : { ...g, units: g.units.map((u, j) => ({ ...u, enabled: resolvedUnits[j] })) }
        );
        persistSelections(session, next);
        return next;
      });
    } finally {
      setApplying(false);
    }
  };

  const allChecked = (group: IGroup, name: string) =>
    group.units.length > 0 && group.units.every((u) => u.enabled.has(name));

  // Клик по заблокированной (обязательной) опции — поясняем, почему её нельзя снять.
  const showMandatoryNote = () =>
    message.info('Входит в стандартную комплектацию модели');

  const save = async () => {
    const payload: Any[] = [];
    groups.forEach((g) =>
      g.units.forEach((u) =>
        g.options.forEach((o) =>
          payload.push({
            picker_option_id: u.pickerOptionId,
            configuration_item_id: o.configurationItemId,
            enabled: o.mandatory || u.enabled.has(o.name),
            // last:false — apply только пишет шаги, без AhpService.
            // Подбор модификации делает find_modifications ниже (иначе AHP считается дважды).
            last: false,
          })
        )
      )
    );
    if (!payload.length) return;
    setSubmitting(true);
    try {
      await setConfiguration({ session, data: payload }).unwrap();
      // Очистить корзину, чтобы заявка содержала только текущую сборку
      // (guest-uuid постоянный, иначе подтягиваются позиции прошлых заявок).
      // Best-effort: эндпоинт отдаёт 404, если корзины ещё нет — это не ошибка,
      // поэтому НЕ unwrap (иначе 404 прервал бы всё оформление).
      await clearShoppingCart(session);
      // Проставить модификацию на picker_options (find_modifications — пока пикер editable),
      // затем пометить сконфигурированным и положить в корзину.
      for (const g of groups) {
        await applyConfiguration({ session, id: g.pickerId }).unwrap();
        await markBunkerAsConfigured({
          id: g.pickerId,
          configured: true,
          session,
        }).unwrap();
        await addToShoppingCart({ id: String(g.pickerId), session }).unwrap();
      }
      // Без уведомления: заявка ещё НЕ отправлена — это лишь переход к форме оформления.
      navigate('/checkout');
    } catch {
      notification.error({
        message: 'Не удалось оформить заявку. Попробуйте ещё раз.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Пока грузим/перезапрашиваем — показываем спиннер, а не устаревший набор.
  if (isLoading || isFetching) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spin size="large" tip="Загружаем опции…">
          <div className="p-12" />
        </Spin>
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="container py-10">
        <p>Нет позиций для конфигурации.</p>
        <Button className="mt-4" onClick={() => navigate('/configurator')}>
          Назад в каталог
        </Button>
      </div>
    );
  }

  return (
    <div className="container pb-[120px]">
      <h2 className="font-semibold text-[20px] my-[24px]">
        Подбор опций по выбранным моделям
      </h2>

      <Spin spinning={applying} tip="Подбираем модификацию…">
      <Collapse
        defaultActiveKey={groups.map((_, i) => String(i))}
        items={groups.map((group, gi) => ({
          key: String(gi),
          label: (
            <span className="font-semibold">
              {group.model}{' '}
              <span className="font-normal text-[#5a5a5a]">
                — {group.units.length} шт
              </span>
            </span>
          ),
          children: (
            // Ограниченный по высоте бокс — единый контейнер прокрутки по обеим осям:
            // тогда шапка липнет к его верху (24), а первая колонка — к левому краю (23),
            // и горизонтальный скролл идёт внутри контейнера, а не режет страницу (21/22).
            <div className="overflow-auto max-h-[70vh] border border-[#E5E7EB] rounded-lg">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-2 sticky top-0 left-0 z-20 bg-white">
                      Бункер
                    </th>
                    {group.options.map((o) => (
                      <th key={o.name} className="p-2 text-center min-w-[120px] align-top sticky top-0 z-10 bg-white">
                        <div className="flex items-start justify-center gap-1">
                          <span
                            title="Подробнее о пользе опции"
                            onClick={() => setInfoOption(o)}
                            className="inline-block cursor-pointer transition duration-200 hover:text-[#0870E2] hover:underline hover:scale-105">
                            {o.displayName}
                          </span>
                          {o.mandatory && (
                            <LockOutlined
                              title="Входит в стандартную комплектацию модели"
                              className="shrink-0 mt-[2px] text-[#9CA3AF] text-[14px]"
                            />
                          )}
                          <InfoCircleOutlined
                            title="Подробнее о пользе опции"
                            onClick={() => setInfoOption(o)}
                            className="shrink-0 mt-[2px] text-[#0870E2] text-[16px] cursor-pointer hover:opacity-70"
                          />
                        </div>
                        {showPrices && o.cost > 0 && (
                          <div className="mt-1 text-[12px] font-semibold text-[#007E3C]">
                            +{addSpacesInNumbers(o.cost)} ₽
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Выбрать для всех */}
                  <tr className="bg-[#EEF7F1]">
                    <td className="p-2 font-semibold sticky left-0 z-10 bg-[#EEF7F1]">
                      Выбрать для всех
                    </td>
                    {group.options.map((o) => (
                      <td
                        key={o.name}
                        className="p-2 text-center"
                        title={
                          o.mandatory
                            ? 'Входит в стандартную комплектацию модели'
                            : undefined
                        }>
                        <OptionSelector
                          checked={o.mandatory || allChecked(group, o.name)}
                          changeState={
                            o.mandatory
                              ? showMandatoryNote
                              : (v) => toggleAll(gi, o.name, v)
                          }
                        />
                      </td>
                    ))}
                  </tr>
                  {/* Построчно юниты */}
                  {group.units.map((unit, ui) => (
                    <tr key={unit.pickerOptionId} className="border-t">
                      <td className="p-2 sticky left-0 z-10 bg-white">
                        {group.model.replace(/Бункер-перегрузчик Лилиани\s*/, '')} #
                        {ui + 1}
                      </td>
                      {group.options.map((o) => (
                        <td
                          key={o.name}
                          className="p-2 text-center"
                          title={
                            o.mandatory
                              ? 'Входит в стандартную комплектацию модели'
                              : undefined
                          }>
                          <OptionSelector
                            checked={o.mandatory || unit.enabled.has(o.name)}
                            changeState={
                              o.mandatory
                                ? showMandatoryNote
                                : () => toggleUnit(gi, ui, o.name)
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ),
        }))}
      />
      </Spin>

      {/* sticky: держится у низа, паркуется над футером; группу сдвигаем к правому краю
          окна отрицательным margin (без растяжения — иначе горизонтальная прокрутка). */}
      <div className="sticky bottom-6 z-40 flex justify-end pointer-events-none">
        <div
          className="pointer-events-auto flex gap-3"
          style={{ marginRight: 'calc(2.5rem - (100vw - 100%) / 2)' }}>
          <Button size="large" onClick={() => navigate('/configurator')}>
            Назад
          </Button>
          <Button
            type="primary"
            size="large"
            loading={submitting}
            disabled={submitting}
            style={{ background: '#66B28A', height: 56, paddingInline: 32 }}
            onClick={save}>
            Оформить заявку
          </Button>
        </div>
      </div>

      {infoOption && (
        // Единая модалка: контент опции (картинка/польза/описание) берётся из БД
        // и редактируется в /admin/options.
        <Modal
          open
          title={infoOption.displayName}
          footer={null}
          width={1000}
          onCancel={() => setInfoOption(null)}>
          {infoOption.imageUrl && (
            <img
              src={infoOption.imageUrl}
              className="w-full mb-4 object-contain max-h-[320px]"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          {!!infoOption.profitItems?.length && (
            <ul className="list-disc ml-5 mb-3 font-light">
              {infoOption.profitItems.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          )}
          <p className="font-light">
            {infoOption.description &&
            infoOption.description !== infoOption.displayName
              ? infoOption.description
              : 'Подробное описание пользы этой опции появится позже.'}
          </p>
        </Modal>
      )}
    </div>
  );
};
